import { useCallback } from 'react'
import type { JSX } from 'react'

import { Button, Flex, Notice } from '@buffer-mono/popcorn'

import { formatCompactNumber } from '~publish/helpers/numberFormatters'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

import { useMentionsCounterMode } from '../../hooks/mentions/useMentionsCounterMode'

import styles from './MentionsCounterPromoBanner.module.css'

const BANNER_ID = 'mentions-counter-promo-banner'

/**
 * Above this many unanswered mentions (for the current scope) we promote dot
 * mode. Matches the ">50" threshold in the ticket (a bubble showing 51+).
 */
export const MENTIONS_PROMO_THRESHOLD = 50

type MentionsCounterPromoBannerProps = {
  /**
   * Unanswered mention count for the current scope — the aggregate on the
   * All Channels view, or the single channel's count on a channel view. The
   * caller owns the scope so the banner surfaces per the ticket's per-view
   * rules (Case 1 / Case 2).
   */
  count: number
}

/**
 * Promotes dot mode to users drowning in mentions. Shown only while in counter
 * mode and once the scope crosses {@link MENTIONS_PROMO_THRESHOLD}. Accepting
 * switches the account to dot mode (mentions leave the general counter);
 * either action dismisses the banner for good.
 */
export function MentionsCounterPromoBanner({
  count,
}: MentionsCounterPromoBannerProps): JSX.Element | null {
  const banner = useDismissableBanner(BANNER_ID, { trackDismiss: true })
  const { mode, setMode } = useMentionsCounterMode()

  const handleRemoveFromCounter = useCallback((): void => {
    setMode('dot')
    banner.dismiss()
  }, [setMode, banner])

  const handleNotNow = useCallback((): void => {
    banner.dismiss()
  }, [banner])

  if (
    mode !== 'counter' ||
    !banner.isActive ||
    count <= MENTIONS_PROMO_THRESHOLD
  ) {
    return null
  }

  return (
    // Grid mirrors the mentions feed so the banner aligns with the cards below.
    <div className={styles.layout}>
      <Notice variant="neutral" icon={false} className={styles.banner}>
        <Notice.Text>
          <strong>
            {formatCompactNumber(count, 99)} mentions - that&apos;s a lot to
            keep up with.
          </strong>{' '}
          Want to remove them from your general counter? You&apos;ll still see a
          dot next to the Mentions tab when new ones appear.
        </Notice.Text>
        <Flex justify="end" gap="xs" fullWidth className={styles.actions}>
          <Button
            size="medium"
            variant="secondary"
            onClick={handleRemoveFromCounter}
          >
            Remove from Counter
          </Button>
          <Button size="medium" variant="tertiary" onClick={handleNotNow}>
            Not Now
          </Button>
        </Flex>
      </Notice>
    </div>
  )
}

MentionsCounterPromoBanner.displayName = 'MentionsCounterPromoBanner'
